/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
// State Manager Component for Club Penguin Game
// Tracks player interactions, visited locations, completed actions, etc.

interface GameState {
  visitedRooms: Set<string>;
  interactedHotspots: Set<string>;
  completedActions: Set<string>;
  playerStats: {
    totalRoomVisits: number;
    totalHotspotInteractions: number;
    sessionStartTime: number;
    lastSaveTime: number;
  };
  customFlags: Map<string, any>;
}

export class StateManager {
  private state: GameState;
  private storageKey: string = 'club-penguin-game-state';

  constructor() {
    this.state = this.initializeState();
    // this.loadFromStorage();
  }

  private initializeState(): GameState {
    return {
      visitedRooms: new Set<string>(),
      interactedHotspots: new Set<string>(),
      completedActions: new Set<string>(),
      playerStats: {
        totalRoomVisits: 0,
        totalHotspotInteractions: 0,
        sessionStartTime: Date.now(),
        lastSaveTime: Date.now(),
      },
      customFlags: new Map<string, any>(),
    };
  }

  // Room tracking
  public visitRoom(roomId: string): boolean {
    const isFirstVisit = !this.state.visitedRooms.has(roomId);
    this.state.visitedRooms.add(roomId);
    this.state.playerStats.totalRoomVisits++;

    if (isFirstVisit) {
      console.log(`First time visiting: ${roomId}`);
    }

    this.saveToStorage();
    return isFirstVisit;
  }

  public hasVisitedRoom(roomId: string): boolean {
    return this.state.visitedRooms.has(roomId);
  }

  public getVisitedRooms(): string[] {
    return Array.from(this.state.visitedRooms);
  }

  // Hotspot interaction tracking
  public interactWithHotspot(hotspotId: string): boolean {
    const isFirstInteraction = !this.state.interactedHotspots.has(hotspotId);
    this.state.interactedHotspots.add(hotspotId);
    this.state.playerStats.totalHotspotInteractions++;

    if (isFirstInteraction) {
      console.log(`First time interacting with hotspot: ${hotspotId}`);
    }

    this.saveToStorage();
    return isFirstInteraction;
  }

  public hasInteractedWithHotspot(hotspotId: string): boolean {
    return this.state.interactedHotspots.has(hotspotId);
  }

  // Action completion tracking
  public completeAction(actionId: string): boolean {
    const isFirstCompletion = !this.state.completedActions.has(actionId);
    this.state.completedActions.add(actionId);

    if (isFirstCompletion) {
      console.log(`Action completed for first time: ${actionId}`);
    }

    this.saveToStorage();
    return isFirstCompletion;
  }

  public hasCompletedAction(actionId: string): boolean {
    return this.state.completedActions.has(actionId);
  }

  // Custom flags for game-specific logic
  public setFlag(key: string, value: any): void {
    this.state.customFlags.set(key, value);
    this.saveToStorage();
  }

  public getFlag(key: string, defaultValue: any = null): any {
    return this.state.customFlags.get(key) ?? defaultValue;
  }

  public hasFlag(key: string): boolean {
    return this.state.customFlags.has(key);
  }

  public removeFlag(key: string): boolean {
    const existed = this.state.customFlags.delete(key);
    if (existed) {
      this.saveToStorage();
    }
    return existed;
  }

  // Statistics
  public getStats() {
    return {
      ...this.state.playerStats,
      uniqueRoomsVisited: this.state.visitedRooms.size,
      uniqueHotspotsInteracted: this.state.interactedHotspots.size,
      actionsCompleted: this.state.completedActions.size,
      sessionDuration: Date.now() - this.state.playerStats.sessionStartTime,
    };
  }

  // Utility methods
  public isFirstTimePlayer(): boolean {
    return this.state.visitedRooms.size === 0 && this.state.playerStats.totalRoomVisits === 0;
  }

  public isExperiencedPlayer(): boolean {
    return (
      this.state.visitedRooms.size >= 3 && this.state.playerStats.totalHotspotInteractions >= 5
    );
  }

  // Persistence
  private saveToStorage(): void {
    try {
      this.state.playerStats.lastSaveTime = Date.now();

      // Convert Sets and Maps to arrays/objects for JSON serialization
      const serializable = {
        visitedRooms: Array.from(this.state.visitedRooms),
        interactedHotspots: Array.from(this.state.interactedHotspots),
        completedActions: Array.from(this.state.completedActions),
        playerStats: this.state.playerStats,
        customFlags: Object.fromEntries(this.state.customFlags),
      };

      localStorage.setItem(this.storageKey, JSON.stringify(serializable));
    } catch (error) {
      console.warn('Failed to save game state:', error);
    }
  }

  private loadFromStorage(): void {
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (!saved) return;

      const data = JSON.parse(saved);

      // Restore Sets and Maps from arrays/objects
      this.state.visitedRooms = new Set(data.visitedRooms || []);
      this.state.interactedHotspots = new Set(data.interactedHotspots || []);
      this.state.completedActions = new Set(data.completedActions || []);
      this.state.playerStats = {
        ...this.state.playerStats,
        ...data.playerStats,
      };
      this.state.customFlags = new Map(Object.entries(data.customFlags || {}));

      console.log('Game state loaded from storage');
    } catch (error) {
      console.warn('Failed to load game state:', error);
    }
  }

  // Debug and admin methods
  public clearAllState(): void {
    this.state = this.initializeState();
    localStorage.removeItem(this.storageKey);
    console.log('Game state cleared');
  }

  public exportState(): string {
    return JSON.stringify(
      {
        visitedRooms: Array.from(this.state.visitedRooms),
        interactedHotspots: Array.from(this.state.interactedHotspots),
        completedActions: Array.from(this.state.completedActions),
        playerStats: this.state.playerStats,
        customFlags: Object.fromEntries(this.state.customFlags),
      },
      null,
      2,
    );
  }

  public importState(stateJson: string): boolean {
    try {
      const data = JSON.parse(stateJson);
      this.state.visitedRooms = new Set(data.visitedRooms || []);
      this.state.interactedHotspots = new Set(data.interactedHotspots || []);
      this.state.completedActions = new Set(data.completedActions || []);
      this.state.playerStats = { ...this.state.playerStats, ...data.playerStats };
      this.state.customFlags = new Map(Object.entries(data.customFlags || {}));
      this.saveToStorage();
      console.log('Game state imported successfully');
      return true;
    } catch (error) {
      console.error('Failed to import game state:', error);
      return false;
    }
  }

  // Get current state snapshot (for debugging)
  public getStateSnapshot() {
    return {
      visitedRooms: Array.from(this.state.visitedRooms),
      interactedHotspots: Array.from(this.state.interactedHotspots),
      completedActions: Array.from(this.state.completedActions),
      playerStats: { ...this.state.playerStats },
      customFlags: Object.fromEntries(this.state.customFlags),
    };
  }
}
